import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useUsers } from "@/hooks/useUsers";
import { useOrganisations } from "@/hooks/useOrganisations";
import { useDevices } from "@/hooks/useDevices";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
}

type SearchTarget =
  | { type: "user"; id: number }
  | { type: "device"; id: number }
  | { type: "organisation"; id: number };

export default function SearchDialog({ open, onClose }: SearchDialogProps) {
  const navigate = useNavigate();
  const { data: users } = useUsers();
  const { data: organisations } = useOrganisations();
  const { data: devices } = useDevices();

  const options = useMemo<ComboboxOption<SearchTarget>[]>(() => {
    const userOptions =
      users?.map((user) => ({ label: user.email, value: { type: "user" as const, id: user.id } })) ?? [];
    const deviceOptions =
      devices?.map((device) => ({
        label: `${device.name} (${device.dev_eui})`,
        value: { type: "device" as const, id: device.id },
      })) ?? [];
    const organisationOptions =
      organisations?.map((org) => ({
        label: org.name,
        value: { type: "organisation" as const, id: org.id },
      })) ?? [];

    return [...userOptions, ...deviceOptions, ...organisationOptions];
  }, [users, devices, organisations]);

  const handleSelect = (target: SearchTarget | null) => {
    if (!target) return;
    navigate(`/${target.type}/${target.id}`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Search</DialogTitle>
        </DialogHeader>
        <Combobox
          options={options}
          onValueChange={handleSelect}
          placeholder="Search users, devices, organisations..."
          searchPlaceholder="Type to search..."
          emptyText="No results found."
        />
      </DialogContent>
    </Dialog>
  );
}
